import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { select, Store } from '@ngrx/store';
import { MapData, mapDataPerCountry } from './map-data';
import { MapCircleRenderer } from './map-circle-renderer';
import { MapPolygonRenderer } from './map-polygon-renderer';
import { MapClusterRenderer } from './map-cluster-renderer';
import { selectRealEstateObjects } from '../../state/results/search-results/search-results.selectors';
import {
  AppParametersService,
  AppState,
  RealEstateObject,
  Region,
} from '@immosky/types';
import { combineLatest, Subscription } from 'rxjs';
import { setMapRealEstateObjects } from '../../state/results/map-results/map-results.actions';
import {
  selectRadiusFilterState,
  selectRegionFilters,
} from '@immosky/object-search-filter';
import { ObjectSearchAppParameters } from '../../services/object-search-app-parameters.service';
import { MapMultiPolygonRenderer } from './map-multi-polygon-renderer';
import {
  selectShowSimilarResults,
  selectSimilarResults,
} from '../../state/results/similar-results/similar-results.selectors';
import {
  DefaultMapIconFactory,
  SimilarObjectMapIconFactory,
} from './map-icon-factory';
import { MapMarkerFactory } from './map-marker-factory';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements AfterViewInit, OnDestroy {
  private circleRenderer!: MapCircleRenderer;
  private polygonRenderer!: MapPolygonRenderer;
  private multiPolygonRenderer!: MapMultiPolygonRenderer;
  private searchResultClusterRenderer!: MapClusterRenderer;
  private similarResultClusterRenderer!: MapClusterRenderer;
  private loader!: Loader;

  private readonly mapRestriction: google.maps.MapRestriction;

  private map!: google.maps.Map;
  private bounds!: google.maps.LatLngBounds;

  private regions: Region[] = [];
  private objects: RealEstateObject[] = [];
  private similarObjects: RealEstateObject[] = [];
  private radius = 0;
  private minZoom = 8;
  private readonly gridSize: number;
  private isRadiusFilterActive = false;
  private subscriptions: Subscription[] = [];

  @ViewChild('mapOutlet')
  mapOutlet!: ElementRef<HTMLDivElement>;

  mapData: MapData;

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private appParametersService: AppParametersService<ObjectSearchAppParameters>
  ) {
    this.gridSize = this.appParametersService.get().mapGridSize;

    this.mapData =
      mapDataPerCountry[this.appParametersService.get().countries[0]];
    this.mapRestriction = {
      latLngBounds: this.mapData.movementBounds.bounds,
      strictBounds: false,
    };
  }

  ngAfterViewInit(): void {
    this.loadApi()
      .then(() =>
        this.determineMinZoom(this.mapData.zoomBounds.bounds, {
          width: this.mapOutlet.nativeElement.clientWidth,
          height: this.mapOutlet.nativeElement.clientHeight,
        })
      )
      .then(() => this.createMap())
      .then(() => {
        this.subscriptions.push(
          combineLatest([
            this.store.pipe(select(selectRegionFilters)),
            this.store.pipe(select(selectRadiusFilterState)),
          ]).subscribe(([regions, radiusFilterState]) => {
            this.regions = regions;

            if (this.regions.length === 1) {
              this.radius = radiusFilterState.filter.value * 1000;
            } else {
              this.radius = 2000;
            }

            this.isRadiusFilterActive = radiusFilterState.isActive;

            this.updateShapes();
          })
        );

        this.subscriptions.push(
          this.store
            .pipe(select(selectRealEstateObjects))
            .subscribe((objects: RealEstateObject[]) => {
              this.objects = objects;
              this.updateSearchResultMarkers();
            })
        );

        this.subscriptions.push(
          combineLatest([
            this.store.pipe(select(selectSimilarResults)),
            this.store.pipe(select(selectShowSimilarResults)),
          ]).subscribe(([objects, showSimilarResults]) => {
            if (showSimilarResults) {
              this.similarObjects = objects;
            } else {
              this.similarObjects = [];
            }
            this.updateSimilarResultMarkers();
          })
        );
      });
  }

  ngOnDestroy() {
    this.loader.deleteScript();

    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  private loadApi(): Promise<typeof google> {
    this.loader = new Loader({
      id: 'google-maps',
      apiKey: this.appParametersService.get().googleMapsApiKey,
      version: 'weekly',
      language: this.appParametersService.get().language,
    });

    return this.loader.load();
  }

  private createMap() {
    this.map = new google.maps.Map(this.mapOutlet.nativeElement, {
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      restriction: this.mapRestriction,
      center: this.mapData.movementBounds.center,
      minZoom: this.minZoom,
      zoom: this.minZoom,
      gestureHandling: 'greedy',
    });

    this.map.addListener('click', () => {
      this.setMapObjects([]);
      this.searchResultClusterRenderer.clearSelected();
    });

    this.circleRenderer = new MapCircleRenderer(this.map);
    this.polygonRenderer = new MapPolygonRenderer(this.map);
    this.multiPolygonRenderer = new MapMultiPolygonRenderer(this.map);

    const defaultIconFactory = new DefaultMapIconFactory();
    this.searchResultClusterRenderer = new MapClusterRenderer({
      map: this.map,
      gridSize: this.gridSize,
      iconFactory: defaultIconFactory,
      markerFactory: new MapMarkerFactory(defaultIconFactory),
    });
    this.searchResultClusterRenderer.selectedObjects.subscribe(
      (objects: RealEstateObject[]) => {
        this.setMapObjects(objects);
      }
    );

    const similarObjectsIconFactory = new SimilarObjectMapIconFactory();
    this.similarResultClusterRenderer = new MapClusterRenderer({
      map: this.map,
      gridSize: this.gridSize,
      iconFactory: similarObjectsIconFactory,
      markerFactory: new MapMarkerFactory(similarObjectsIconFactory),
    });
    this.similarResultClusterRenderer.selectedObjects.subscribe(
      (objects: RealEstateObject[]) => {
        this.setMapObjects(objects);
      }
    );
  }

  /**
   * Code from https://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds
   * @param bounds
   * @param mapDim
   * @private
   */
  private determineMinZoom(
    bounds: google.maps.LatLngBoundsLiteral,
    mapDim: { width: number; height: number }
  ) {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat: number) {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = new google.maps.LatLng({ lat: bounds.north, lng: bounds.east });
    const sw = new google.maps.LatLng({ lat: bounds.south, lng: bounds.west });

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    this.minZoom = Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  private setMapObjects(objects: RealEstateObject[]) {
    this.store.dispatch(
      setMapRealEstateObjects({
        objects,
      })
    );
  }

  private updateShapes() {
    this.multiPolygonRenderer.clear();
    this.polygonRenderer.clear();
    this.circleRenderer.clear();

    this.circleRenderer.update(
      this.regions,
      this.radius,
      this.isRadiusFilterActive
    );
    if (this.regions.length > 1 || !this.isRadiusFilterActive) {
      this.polygonRenderer.update(this.regions);
      this.multiPolygonRenderer.update(this.regions);
    }

    this.updateBounds();
  }

  updateSearchResultMarkers() {
    this.searchResultClusterRenderer.update(this.objects);
  }

  updateSimilarResultMarkers() {
    this.similarResultClusterRenderer.update(this.similarObjects);
  }

  private updateBounds() {
    this.bounds = new google.maps.LatLngBounds();

    for (const bound of this.circleRenderer.getBounds()) {
      this.bounds.extend(bound);
    }

    for (const bound of this.polygonRenderer.getBounds()) {
      this.bounds.extend(bound);
    }

    for (const bound of this.multiPolygonRenderer.getBounds()) {
      this.bounds.extend(bound);
    }

    if (!this.bounds.isEmpty()) {
      // have to call twice.
      // if only called once, map sometimes focuses some weird places.
      //
      // WARNING
      // if map is initially display: none; and was never displayed
      // before calling fitBounds, the web page crashes
      this.map.fitBounds(this.bounds, 32);
      this.map.fitBounds(this.bounds, 32);
    }
  }
}
